/**
 * How a disk-alarm recipient list is SPLIT and CHECKED (0203, #574).
 *
 * ── Why this lives in `@papan/types` and not beside the resolver ───────────
 *
 * `readDiskAlarmRecipients` is in `@papan/database`, which pulls in `pg`, so the
 * BROWSER cannot import it, and the console's editor needs the same rules to
 * give immediate feedback. That is the identical reason
 * `resolveLandingExperience` (0162) sits in `shared/types/src/landing.ts` rather
 * than beside its two siblings in the platform-settings repository, and
 * CLAUDE.md records it as the precedent.
 *
 * THREE consumers, and they must not drift:
 *
 *   worker   `readDiskAlarmRecipients`: what the alarm actually sends to
 *   API      the PUT route's validation: what it will accept and store
 *   browser  the console editor: what it tells the operator before they save
 *
 * A second copy is how a route comes to accept a value the alarm cannot use, or
 * a screen comes to reject one the server would have taken. Before this module
 * existed there were three copies of the split and two of the shape check.
 */

/**
 * The most recipients a deployment may configure.
 *
 * `deliverDiskAlarm` sends ONE email PER recipient, all in parallel through
 * `Promise.allSettled`, so the list length is a fan-out factor on a worker that
 * is by definition already having a bad day. A pasted address book would open
 * that many concurrent sends per alarm, rate-limit the provider, and risk
 * reaching nobody at the moment it matters most.
 *
 * 20 is generous for what this is — the handful of people who should be woken
 * by a filling disk — and bounding it follows the same instinct as everything
 * else on this path (`EMAIL_BOUND_MS`, the per-level cooldowns) and the org-name
 * cap already in the same route file. Enforced at the WRITE, so an operator
 * finds out while they are looking at the box, not during an incident.
 */
export const MAX_DISK_ALARM_RECIPIENTS = 20

/**
 * Split a stored recipient list into DISTINCT addresses.
 *
 * Comma-separated and whitespace-tolerant, with empties dropped, so
 * `"a@x.com, , b@x.com"` yields two addresses rather than three with a blank
 * that the mailer would reject at send time, during an incident.
 *
 * DE-DUPLICATED, case-insensitively, first spelling kept. A pasted list
 * assembled from two places routinely repeats someone, and the sender is
 * per-recipient — so a duplicate is a second page for the same human, on a
 * channel this alarm's own comments say "must stay worth reading". Matching
 * case-insensitively because `Ops@x.com` and `ops@x.com` are one mailbox at
 * every real provider; the RFC's case-sensitive local part is a distinction
 * none of them honour, and honouring it here would only mean paging someone
 * twice.
 *
 * A string that parses to NOTHING (`","`, `"  "`) is meaningful: every caller
 * treats it as "not configured" rather than as "email nobody". The column's
 * CHECK stops `''` reaching the database, but not a string of separators.
 */
export function parseDiskAlarmEmails(raw: string | null | undefined): string[] {
  if (!raw) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const e of raw.split(',').map((x) => x.trim())) {
    if (e.length === 0) continue
    const key = e.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(e)
  }
  return out
}

/**
 * A pragmatic shape check, deliberately NOT RFC 5322. The mailer is the real
 * authority. Permissive on purpose: refusing a deliverable address is worse
 * than accepting a typo the operator can see sitting in the box.
 */
const LOOKS_LIKE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function looksLikeEmail(value: string): boolean {
  return LOOKS_LIKE_EMAIL.test(value)
}

/** The entries that fail the shape check, named, so a caller can tell an
 *  operator WHICH pasted address is wrong rather than that "the list" is. */
export function invalidDiskAlarmEmails(raw: string | null | undefined): string[] {
  return parseDiskAlarmEmails(raw).filter((e) => !looksLikeEmail(e))
}
